import React, {useMemo, useState} from "react";
import {Box, Grid2 as Grid, Paper, Typography} from "@mui/material";
import {useTranslation} from "react-i18next";
import {getNextAdvanceStatus, OrderActions, OrderDTO, OrderStatus} from "@/model/order";
import {OrderStatusBadge} from "@/common/order/OrderStatusBadge";
import OrderCancellationDialog from "@/common/order/OrderCancellationDialog";
import OrderRejectDeliveryDateDialog from "@/common/order/OrderRejectDeliveryDateDialog";
import ConfirmationDialog from "@/lib/components/ConfirmationDialog";
import {ButtonEx} from "@/lib/components/buttons/ButtonEx";
import { CommonI18N, OrderI18N } from "@/translations";
import {orderApi} from "@/boxhub-api";
import {useSnackbarEx} from "@/lib/snackbarex";
import {useDialogController} from "@/lib/dialog-controller";
import dayjs from "dayjs";
import DateInputField from "@/lib/components/inputfields/date-input-field";
import StringInputField from "@/lib/components/inputfields/string-input-field";
import {FieldError} from "@/lib/utils";
import {DateFormatter} from "@/lib/components/DateFormate";

interface OrderBannerProps {
    order: OrderDTO;
    onStatusAdvanced: () => void;
    onCancelled: () => void;
    onDeliveryDateUpdated: () => void;
    /** When true, hides all action buttons and date input (read-only display). */
    readonly?: boolean;
}

/**
 * Banner displayed at the top of request detail pages when an order exists.
 *
 * Button layout per status:
 * - PENDING:                   "Auftrag bestätigen" + "Auftrag stornieren"                              (Supplier)
 * - ORDER_CONFIRMED:           inline date field + "Liefertermin vorschlagen" + "Auftrag stornieren"    (Supplier)
 * - DELIVERY_DATE_PROPOSED:    "Liefertermin annehmen" + "Liefertermin ablehnen" + "Stornieren"         (Requester)
 *                               "Warten auf Bestätigung..." + "Auftrag stornieren"                     (Supplier)
 * - DELIVERY_DATE_CONFIRMED:   "Zugestellt melden" (with dialog) + "Auftrag stornieren"                (Supplier)
 */
const OrderBanner: React.FC<OrderBannerProps> = ({
                                                     order,
                                                     onStatusAdvanced,
                                                     onCancelled,
                                                     onDeliveryDateUpdated,
                                                     readonly: readonlyProp = false
                                                 }) => {
    const {t} = useTranslation();
    const {showMessage} = useSnackbarEx();
    const [DialogNode, showDialog] = useDialogController();
    const [deliveryDate, setDeliveryDate] = useState<string | null>(null);

    const allowedActions: OrderActions[] = order.allowedActions ?? [];
    const canAdvance = allowedActions.includes(OrderActions.ADVANCE_STATUS);
    const canEditDelivery = allowedActions.includes(OrderActions.EDIT_DELIVERY_DATE);
    const canCancel = allowedActions.includes(OrderActions.CANCEL_ORDER);
    const canConfirmDeliveryDate = allowedActions.includes(OrderActions.CONFIRM_DELIVERY_DATE);
    const canRejectDeliveryDate = allowedActions.includes(OrderActions.REJECT_DELIVERY_DATE);
    const nextStatus = order.status ? getNextAdvanceStatus(order.status) : undefined;

    // canEditDelivery is true only in ORDER_CONFIRMED.
    // PENDING: canAdvance=true, canEditDelivery=false → "Auftrag bestätigen"
    // ORDER_CONFIRMED: canAdvance=true, canEditDelivery=true → "Liefertermin vorschlagen"
    // DELIVERY_DATE_CONFIRMED: canAdvance=true, canEditDelivery=false → "Zugestellt melden"
    const isPending = order.status === OrderStatus.PENDING;

    const today = useMemo(() => dayjs().startOf("day"), []);
    const maxDeliveryDate = useMemo(
            () => (order.maxDeliveryDate ? dayjs(order.maxDeliveryDate) : undefined),
            [order.maxDeliveryDate]
    );

    // Delivery date is valid when the inline field has a value or the order already has one saved.
    // The already-saved order.deliveryDate was validated by the backend when stored.
    const hasDeliveryDate = !!deliveryDate || !!order.deliveryDate;

    // If a new date was entered, it must be between today and the configured max delivery date.
    const isNewDateInRange =
            !deliveryDate ||
            (!dayjs(deliveryDate).isBefore(today, "day") &&
                    (!maxDeliveryDate || !dayjs(deliveryDate).isAfter(maxDeliveryDate, "day")));

    const canConfirmDelivery = hasDeliveryDate && isNewDateInRange;

    const getDeliveryDateError = (): FieldError | undefined => {
        if (!deliveryDate) return undefined;
        if (dayjs(deliveryDate).isBefore(today, "day")) {
            return {
                message: t(OrderI18N.banner.validation.deliveryDateInPast, {
                    date: today.format(DateFormatter("date"))
                })
            };
        }
        if (maxDeliveryDate && dayjs(deliveryDate).isAfter(maxDeliveryDate, "day")) {
            return {
                message: t(OrderI18N.banner.validation.deliveryDateTooFarInFuture, {
                    date: maxDeliveryDate.format(DateFormatter("date"))
                })
            };
        }
        return undefined;
    };
    const deliveryDateError = getDeliveryDateError();

    /**
     * Handles "Auftrag bestätigen" (PENDING → ORDER_CONFIRMED).
     * No delivery date involved at this stage.
     */
    const handleConfirmOrder = () => {
        const orderId = order.id;
        if (!orderId || !nextStatus) return;
        showDialog((open, close) => (
                <ConfirmationDialog
                        open={open}
                        onClose={close}
                        onConfirm={async () => {
                            try {
                                await orderApi.advanceStatus(orderId, {targetStatus: nextStatus});
                                onStatusAdvanced();
                            } catch {
                                showMessage({summary: t(CommonI18N.message.unknownError), severity: "error"});
                            }
                        }}
                        title={t(OrderI18N.confirmOrder.dialog.title)}
                        message={t(OrderI18N.confirmOrder.dialog.message)}
                        confirmButtonText={t(CommonI18N.action.save)}
                        cancelButtonText={t(CommonI18N.action.cancel)}
                        closeOnConfirm
                />
        ));
    };

    /**
     * Handles "Liefertermin vorschlagen" (ORDER_CONFIRMED → DELIVERY_DATE_PROPOSED).
     * Opens a confirmation dialog; on confirm, saves the inline delivery date (if entered) and advances the status.
     */
    const handleConfirmDelivery = () => {
        const orderId = order.id;
        if (!orderId || !nextStatus) return;
        showDialog((open, close) => (
                <ConfirmationDialog
                        open={open}
                        onClose={close}
                        onConfirm={async () => {
                            try {
                                if (deliveryDate) {
                                    await orderApi.updateDeliveryDate(orderId, {deliveryDate});
                                    onDeliveryDateUpdated();
                                }
                                await orderApi.advanceStatus(orderId, {targetStatus: nextStatus});
                                onStatusAdvanced();
                            } catch {
                                showMessage({summary: t(CommonI18N.message.unknownError), severity: "error"});
                            }
                        }}
                        title={t(OrderI18N.confirmDeliveryProposal.dialog.title)}
                        message={t(OrderI18N.confirmDeliveryProposal.dialog.message)}
                        confirmButtonText={t(CommonI18N.action.save)}
                        cancelButtonText={t(CommonI18N.action.cancel)}
                        closeOnConfirm
                />
        ));
    };

    /**
     * Handles "Zugestellt melden" (DELIVERY_DATE_CONFIRMED → DELIVERED).
     * Opens a confirmation dialog before advancing.
     */
    const handleConfirmDelivered = () => {
        const orderId = order.id;
        if (!orderId || !nextStatus) return;
        showDialog((open, close) => (
                <ConfirmationDialog
                        open={open}
                        onClose={close}
                        onConfirm={async () => {
                            try {
                                await orderApi.advanceStatus(orderId, {targetStatus: nextStatus});
                                onStatusAdvanced();
                            } catch {
                                showMessage({summary: t(CommonI18N.message.unknownError), severity: "error"});
                            }
                        }}
                        title={t(OrderI18N.confirmDelivered.dialog.title)}
                        message={t(OrderI18N.confirmDelivered.dialog.message)}
                        confirmButtonText={t(CommonI18N.action.save)}
                        cancelButtonText={t(CommonI18N.action.cancel)}
                        closeOnConfirm
                />
        ));
    };

    /**
     * Handles requester confirming the proposed delivery date (DELIVERY_DATE_PROPOSED → DELIVERY_DATE_CONFIRMED).
     */
    const handleConfirmDeliveryDate = () => {
        const orderId = order.id;
        if (!orderId) return;
        showDialog((open, close) => (
                <ConfirmationDialog
                        open={open}
                        onClose={close}
                        onConfirm={async () => {
                            try {
                                await orderApi.confirmDeliveryDate(orderId);
                                onStatusAdvanced();
                            } catch {
                                showMessage({summary: t(CommonI18N.message.unknownError), severity: "error"});
                            }
                        }}
                        title={t(OrderI18N.confirmDeliveryDate.dialog.title)}
                        message={t(OrderI18N.confirmDeliveryDate.dialog.message)}
                        confirmButtonText={t(CommonI18N.action.save)}
                        cancelButtonText={t(CommonI18N.action.cancel)}
                        closeOnConfirm
                />
        ));
    };

    /**
     * Handles requester rejecting the proposed delivery date (DELIVERY_DATE_PROPOSED → ORDER_CONFIRMED).
     */
    const handleRejectDeliveryDate = () => {
        const orderId = order.id;
        if (!orderId) return;
        showDialog((open, close) => (
                <OrderRejectDeliveryDateDialog
                        open={open}
                        onClose={close}
                        onConfirm={async (reason) => {
                            try {
                                await orderApi.rejectDeliveryDate(orderId, {reason: reason || undefined});
                                onStatusAdvanced();
                            } catch {
                                showMessage({summary: t(CommonI18N.message.unknownError), severity: "error"});
                            }
                        }}
                />
        ));
    };

    const handleCancelOrder = async (reason: string) => {
        if (!order.id) return;
        try {
            await orderApi.cancelOrder(order.id, {cancellationReason: reason});
            onCancelled();
        } catch {
            showMessage({summary: t(CommonI18N.message.unknownError), severity: "error"});
        }
    };

    const isCancelled =
            order.status === OrderStatus.CANCELLED_BY_SUPPLIER ||
            order.status === OrderStatus.CANCELLED_BY_REQUESTER ||
            order.status === OrderStatus.DEACTIVATED;

    const handleOpenCancellationDialog = () => {
        showDialog((open, close) => (
                <OrderCancellationDialog
                        open={open}
                        onClose={close}
                        onConfirm={(reason) => {
                            close();
                            handleCancelOrder(reason);
                        }}
                />
        ));
    };

    return (
            <>
                <DialogNode/>
                <Paper elevation={3} sx={{p: 2, mb: 2, width: "100%"}}>
                    <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                        <Grid size={{sm: 2}}>
                            <Typography variant="subtitle2" color="text.secondary">
                                {t(OrderI18N.banner.page.title)}
                            </Typography>
                            <Typography variant="h6" fontWeight="bold">
                                {order.orderNumber}
                            </Typography>
                            <Box sx={{mt: 0.5}}>
                                <OrderStatusBadge status={order.status}/>
                            </Box>
                            {order.requestStatus && (
                                    <Typography variant="caption" color="text.secondary"
                                                sx={{display: "block", mt: 0.5}}>
                                        {t(OrderI18N.banner.field.requestStatus, {
                                            status: t(CommonI18N.enum.requestStatus[order.requestStatus])
                                        })}
                                    </Typography>
                            )}
                            {order.deliveryDate && (
                                    <Typography variant="body2" sx={{mt: 0.5}}>
                                        {t(
                                                order.status === OrderStatus.DELIVERY_DATE_PROPOSED
                                                        ? OrderI18N.banner.field.proposedDeliveryDate
                                                        : OrderI18N.banner.field.confirmedDeliveryDate
                                        )}
                                        : {dayjs(order.deliveryDate).format(DateFormatter("date"))}
                                    </Typography>
                            )}
                            {order.status === OrderStatus.DELIVERY_DATE_PROPOSED &&
                                    !canConfirmDeliveryDate &&
                                    !canRejectDeliveryDate && (
                                            <Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>
                                                {t(OrderI18N.banner.message.waitingForRequesterConfirmation)}
                                            </Typography>
                                    )}
                        </Grid>
                        <Grid size={{sm: 2}}>
                            {order.status === OrderStatus.ORDER_CONFIRMED && order.deliveryDateRejectionReason && (
                                    <StringInputField
                                            label={t(OrderI18N.banner.field.deliveryDateRejectionReason)}
                                            value={order.deliveryDateRejectionReason}
                                            multiline
                                            minRows={1}
                                            disabled
                                    />
                            )}
                        </Grid>
                        <Grid size={{sm: 2}}>
                            {isCancelled && order.cancellationReason && (
                                    <StringInputField
                                            label={t(
                                                    order.status === OrderStatus.DEACTIVATED
                                                            ? OrderI18N.banner.field.deactivationReason
                                                            : OrderI18N.banner.field.cancellationReason
                                            )}
                                            value={order.cancellationReason}
                                            multiline
                                            minRows={1}
                                            disabled
                                    />
                            )}
                        </Grid>
                        <Grid sx={{sm: 2}}>
                            {!readonlyProp && canEditDelivery && (
                                    <DateInputField
                                            name="deliveryDate"
                                            label={t(OrderI18N.banner.field.deliveryDate)}
                                            value={deliveryDate}
                                            onChange={(nv) =>
                                                    setDeliveryDate(nv.value ? dayjs(nv.value).format("YYYY-MM-DD") : null)
                                            }
                                            minDate={today}
                                            maxDate={maxDeliveryDate}
                                            error={deliveryDateError}
                                    />
                            )}
                        </Grid>
                        <Grid sx={{sm: 4}}>
                            {!readonlyProp && (
                                    <Grid container spacing={1}>
                                        {/* PENDING: simple advance to ORDER_CONFIRMED, no date required */}
                                        {canAdvance && isPending && (
                                                <Grid>
                                                    <ButtonEx
                                                            label={t(OrderI18N.banner.action.confirmOrder)}
                                                            color="success"
                                                            onClick={handleConfirmOrder}
                                                    />
                                                </Grid>
                                        )}
                                        {/* ORDER_CONFIRMED: inline date + advance to DELIVERY_DATE_PROPOSED */}
                                        {canAdvance && canEditDelivery && (
                                                <Grid>
                                                    <ButtonEx
                                                            label={t(OrderI18N.banner.action.advanceStatus)}
                                                            color="success"
                                                            onClick={handleConfirmDelivery}
                                                            disabled={!canConfirmDelivery}
                                                    />
                                                </Grid>
                                        )}
                                        {/* DELIVERY_DATE_PROPOSED: requester confirms or rejects delivery date */}
                                        {canConfirmDeliveryDate && (
                                                <Grid>
                                                    <ButtonEx
                                                            label={t(OrderI18N.banner.action.confirmDeliveryDate)}
                                                            color="success"
                                                            onClick={handleConfirmDeliveryDate}
                                                    />
                                                </Grid>
                                        )}
                                        {canRejectDeliveryDate && (
                                                <Grid>
                                                    <ButtonEx
                                                            label={t(OrderI18N.banner.action.rejectDeliveryDate)}
                                                            color="warning"
                                                            onClick={handleRejectDeliveryDate}
                                                    />
                                                </Grid>
                                        )}
                                        {/* DELIVERY_DATE_CONFIRMED: advance to DELIVERED with confirmation */}
                                        {canAdvance && !canEditDelivery && !isPending && !canConfirmDeliveryDate && (
                                                <Grid>
                                                    <ButtonEx
                                                            label={t(OrderI18N.banner.action.confirmDelivered)}
                                                            color="success"
                                                            onClick={handleConfirmDelivered}
                                                    />
                                                </Grid>
                                        )}
                                        {canCancel && (
                                                <Grid>
                                                    <ButtonEx
                                                            label={t(OrderI18N.banner.action.cancelOrder)}
                                                            color="error"
                                                            onClick={handleOpenCancellationDialog}
                                                    />
                                                </Grid>
                                        )}
                                    </Grid>
                            )}
                        </Grid>
                    </Grid>
                </Paper>
            </>
    );
};

export default OrderBanner;
